import React, { useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavLink } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import KitesurfingOutlined from '@mui/icons-material/KitesurfingOutlined';
import { PaperWrapper } from 'atoms/PaperWrapper/PaperWrapper';
import { store } from '../store';
import { byDate as eventsByDate, groupSubmissions } from '../Events/api';
import { format, differenceInCalendarDays, addDays, addMonths, startOfDay } from 'date-fns';
import {getEventTypeColour} from '../Events/eventTypeColours';

const shouldForwardProp = (prop) => prop !== 'isPast' && prop !== 'isToday';

const EventItem = styled(NavLink, { shouldForwardProp })(({ isPast, isToday }) => ({
  display: 'block',
  textDecoration: 'none',
  color: 'inherit',
  padding: '8px 0',
  opacity: isPast ? 0.5 : 1,
  borderLeft: isToday ? '3px solid #4caf50' : '3px solid transparent',
  paddingLeft: 8,
  position: 'relative',
  borderTop: '1px solid #f0f0f0',
  '&:hover': {
    backgroundColor: '#f9f9f9'
  }
}));

const EventDetails = styled('div')({
  flex: 1,
  minWidth: 0
});

const EventDateLine = styled(Typography, { shouldForwardProp })(({ isPast, isToday }) => ({
  fontSize: '0.7rem',
  fontWeight: 500,
  color: isPast ? '#aaa' : (isToday ? '#2e7d32' : '#666'),
  marginBottom: 2
}));

function getRelativeLabel(date, today) {
  const diff = differenceInCalendarDays(date, today);
  if (diff === -1) return 'Yesterday';
  if (diff < -1) return `${Math.abs(diff)} days ago`;
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return null;
}

function formatEventDate(startDate, endDate, today) {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;
  const startDay = startOfDay(start);
  const endDay = end ? startOfDay(end) : startDay;
  const startDiff = differenceInCalendarDays(startDay, today);
  const endDiff = differenceInCalendarDays(endDay, today);

  // Started in the past but still ongoing today or later
  const isOngoing = startDiff < 0 && endDiff >= 0;
  const isStartToday = startDiff === 0;

  const relative = isOngoing ? 'Ongoing' : getRelativeLabel(startDay, today);

  const dateStr = isStartToday
    ? format(start, 'h:mma')
    : format(start, 'EEE d MMM, h:mma');
  let result = dateStr;

  if (end && differenceInCalendarDays(end, start) > 0) {
    result += ` – ${format(end, 'EEE d MMM, h:mma')}`;
  } else if (end) {
    result += ` – ${format(end, 'h:mma')}`;
  }

  if (relative) {
    result = `${relative} · ${result}`;
  }

  return result;
}

function EventRow({ event, isPast, isToday }) {
  const { state } = useContext(store);
  const linkTo = `/${state.site?.normalName}/events/view/${event._id}`;

  return (
    <EventItem to={linkTo} isPast={isPast} isToday={isToday} sx={{backgroundColor: getEventTypeColour(state.site, event.type),
      '&:hover': {backgroundColor: getEventTypeColour(state.site, event.type)}}}>
      <EventDateLine isPast={isPast} isToday={isToday}>
        {formatEventDate(event.startDate, event.endDate, startOfDay(new Date()))}
      </EventDateLine>
      <EventDetails>
        <Typography variant="body1" color="text.primary" noWrap>
          {event.eventName || event.groupName || 'Untitled Event'}
        </Typography>
        {event.type && (
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>
            {event.type}
          </Typography>
        )}
        {event.area && (
          <Typography variant="caption" color="text.secondary">
            {event.area}
          </Typography>
        )}
        {(event.sleepers || event.dayGuests) && (
          <Typography variant="caption" display="block" color="text.secondary">
            {event.sleepers ? `${event.sleepers} sleepers` : ''}
            {event.sleepers && event.dayGuests ? ' · ' : ''}
            {event.dayGuests ? `${event.dayGuests} day guests` : ''}
          </Typography>
        )}
      </EventDetails>
      <div style={{ position: 'absolute', bottom: 8, right: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <KitesurfingOutlined sx={{ fontSize: 19, color: event.kite ? '#1866b8' : '#bbb' }} alt="Kites" />
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: event.kite ? '#1866b8' : '#bbb', lineHeight: 1 }}>
          {event.kite || 0}
        </Typography>
      </div>
    </EventItem>
  );
}

function EventsList({ pastEvents, todayEvents, futureEvents }) {
  const allEmpty = !pastEvents.length && !todayEvents.length && !futureEvents.length;

  if (allEmpty) {
    return (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        No upcoming events in the next month.
      </Typography>
    );
  }

  return (
    <div style={{maxHeight: 510, overflow: 'scroll'}}>
      {pastEvents.map(event => (
        <EventRow key={event._id} event={event} isPast />
      ))}
      {todayEvents.map(event => (
        <EventRow key={event._id} event={event} isToday />
      ))}
      {futureEvents.map(event => (
        <EventRow key={event._id} event={event} />
      ))}
    </div>
  );
}

export default function UpcomingEvents() {
  const { state } = useContext(store);
  const siteId = state.site?._id;

  const today = startOfDay(new Date());
  const eventsFrom = format(addDays(today, -2), 'yyyy-MM-dd');
  const eventsTo = format(addMonths(today, 1), 'yyyy-MM-dd');

  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['presenceEvents', siteId, eventsFrom, eventsTo],
    queryFn: () => eventsByDate({ from: eventsFrom, to: eventsTo, site: siteId }),
    enabled: !!siteId,
    refetchOnWindowFocus: false
  });

  const { data: groupsProfiles } = useQuery({
    queryKey: ['presenceKiteCount', siteId],
    queryFn: () => groupSubmissions({ site: siteId, group: 'eventId', fields: 'count' }),
    enabled: !!siteId,
    refetchOnWindowFocus: false
  });

  const { pastEvents, todayEvents, futureEvents } = useMemo(() => {
    const events = eventsData?.data || [];
    const kiteCounts = groupsProfiles?.data || [];
    events.forEach(event => {
      kiteCounts.forEach(group => {
        if (group._id?.eventId === event._id) {
          event.kite = group.count;
        }
      });
    });
    const sorted = [...events].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    const past = [];
    const current = [];
    const future = [];
    const pastCutoff = addDays(today, -2);

    sorted.forEach(event => {
      const start = startOfDay(new Date(event.startDate));
      const end = event.endDate ? startOfDay(new Date(event.endDate)) : start;
      const startDiff = differenceInCalendarDays(start, today);
      const endDiff = differenceInCalendarDays(end, today);

      if (endDiff >= 0 && startDiff <= 0) {
        // Still ongoing today (started in past or today, ends today or later)
        current.push(event);
      } else if (startDiff > 0) {
        future.push(event);
      } else if (start >= pastCutoff) {
        // Ended in the past, only show if within last 3 days
        past.push(event);
      }
    });

    return { pastEvents: past, todayEvents: current, futureEvents: future };
  }, [eventsData, groupsProfiles, today]);

  return (
    <PaperWrapper heading={'Events'} loading={isLoading}>
      {!isLoading && (
        <EventsList
          pastEvents={pastEvents}
          todayEvents={todayEvents}
          futureEvents={futureEvents}
        />
      )}
    </PaperWrapper>
  );
}
